import { defineComponent as _defineComponent } from 'vue'
import Triangle from '@/components/svg-components/Triangle.vue';
import ThreadRepliesSkeleton from '@/views/forum/components/ThreadRepliesSkeleton.vue';
import {
  computed, nextTick, onMounted, onUpdated, ref, toRefs, watch
} from 'vue';
import useUser from '@/mixins/useUser';
import { Message, MessagePost } from '@/views/forum/models/Forum';
import { useStore } from 'vue2-helpers/vuex';
import useProjectGuestState from '@/state/projectGuestState';
import { useEventListener } from '@vueuse/core';
import { useRoute } from 'vue2-helpers/vue-router';
import useI18n from '@/mixins/useI18n';
import BCXSwitch from '@/components/molecules/BCXSwitch.vue';
import ThreadRepliesExpansionPanel from '@/views/forum/components/ThreadRepliesExpansionPanel.vue';
import CreateMessage from './CreateMessage.vue';
import ThreadReply from './ThreadReply.vue';

interface Props {
  replies: Array<Message>;
  threadCreator: any;
  initialMessage: Message;
  disableReply?: boolean;
  forumId: string;
  groupId: string;
  threadId: string;
  editMessageId: string;
  sortParam: string;
  sortOrder: string;
  numberOfReplies: number;
  threadSubscribed: boolean;
  parentType: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ThreadReplies',
  props: {
    replies: { default: () => [] },
    threadCreator: { default: () => ({
    userId: '', firstname: '', lastname: '', fullname: ''
  }) },
    initialMessage: { default: () => ({
    created: '',
    creator: { userId: '', fullname: '' },
    messageContent: '',
    messageId: '',
    downvoteCount: 0,
    upvoteCount: 0,
    text: '',
    voteResult: 0
  }) },
    disableReply: { type: Boolean, default: false },
    forumId: { default: undefined },
    groupId: { default: undefined },
    threadId: { default: undefined },
    editMessageId: { default: '' },
    sortParam: { default: 'TIMESTAMP' },
    sortOrder: { default: 'DESC' },
    numberOfReplies: { default: 0 },
    threadSubscribed: { type: Boolean, default: false },
    parentType: { default: 'FORUM_THREAD' }
  },
  emits: ['post-message', 'update-message', 'edit-message', 'sort-replies', 'subscribe', 'unsubscribe', 'scrolled-to-bottom'],
  setup(__props: any, { emit }) {

const props = __props as { replies: Array<Message>, threadCreator: any, initialMessage: Message, disableReply: boolean, forumId: string, groupId: string, threadId: string, editMessageId: string, sortParam: string, sortOrder: string, numberOfReplies: number, threadSubscribed: boolean, parentType: string };





const store = useStore();
const route = useRoute();
const { tc, t } = useI18n();
const { tUserFallback: tUser } = useUser('self');
const project = computed(() => store.state.projectForum.project);
const createMessageVisible = ref(false);
const scrolledToMessage = ref(false);
const watchThread = ref(false);
const user = useUser('self');

const {
  threadCreator: threadCreatorProp,
  numberOfReplies: numberOfRepliesProp,
  parentType,
  threadSubscribed,
  forumId,
  threadId,
  initialMessage
} = toRefs(props);

watch(
  () => threadSubscribed.value,
  (newValue) => {
    watchThread.value = newValue;
  }
);

const labelReplyCount = computed(() => tc('forum.labels.replies', numberOfRepliesProp.value));
const noForumReplies = computed(() => numberOfRepliesProp.value === 0);
// const noForumReplies = ref(false);
const isUserCreator = computed(() => store.getters['user'].userId === threadCreatorProp.value?.userId);
const watchThreadTranslationKey = computed(() => {
  if (parentType.value === 'PROJECT_THREAD') {
    return 'project-forum_USERTYPE.labels.watch-project';
  }
  return 'forum.labels.watch-thread';
});
const NoRepliesTranslationKey = computed(() => {
  if (parentType.value === 'PROJECT_THREAD') {
    return 'forum.no_replies_project';
  }
  return 'forum.no_replies';
});
const isTestProject = computed(() => project.value.metaDataProject.testFlag || false);

const hasReplies = (reply: Message): boolean => (reply?.subMessages?.length || 0) > 0;
const hasOPReply = (replies: Array<Message>): boolean => {
  if (!threadCreatorProp.value?.userId) return false;
  return replies.some((reply) => reply.creator.userId === threadCreatorProp.value.userId);
};
const toggleCreateMessageVisible = (): void => {
  createMessageVisible.value = !createMessageVisible.value;
};
const { projectGuestList } = useProjectGuestState(project.value?.company?.id);
const isOP = (userId: string): boolean => {
  if (!threadCreatorProp.value?.userId) return false;
  return threadCreatorProp.value.userId === userId || projectGuestList.value.some((guest) => guest.id === userId);
};

useEventListener('scroll', () => {
  const bottomOfWindow = document.documentElement.scrollTop + window.innerHeight >= document.documentElement.offsetHeight - 200;
  if (bottomOfWindow) {
    emit('scrolled-to-bottom');
  }
});

const expansionPanelStateChange = (state: string, id: string) => {
  const el = document.querySelector(`#inner-replies-${id}`);
  if (el) {
    switch (state) {
      case 'open':
        el.classList.add('expansion-panel--open');
        break;
      case 'close':
      default:
        el.classList.remove('expansion-panel--open');
        break;
    }
  }
};

const getReplyText = (replyCount: number): string => `${replyCount} ${tc('forum.labels.replies', replyCount) as string}`;

const postMessage = (postMessage: MessagePost) => {
  emit('post-message', postMessage);
  createMessageVisible.value = false;
};

const firstReply = (message: string) => {
  const messageObject: MessagePost = {
    forumId: forumId.value,
    forumThreadId: threadId.value,
    replyToUserId: initialMessage.value.creator.userId || '',
    replyToMessageId: initialMessage.value.messageId,
    messageContent: message
  };
  postMessage(messageObject);
};

const updateMessage = (postMessage: MessagePost, messageCount: string) => {
  emit('update-message', postMessage, messageCount);
};

const editMessage = (editMessageId: string) => {
  emit('edit-message', editMessageId);
};

const sort = (sortParam: string) => {
  let sortOrder = null;
  if (sortParam === props.sortParam && props.sortOrder === 'ASC') {
    sortOrder = 'DESC';
  } else if (sortParam === props.sortParam && props.sortOrder === 'DESC') {
    sortOrder = 'ASC';
  } else if (sortParam === 'VOTES') {
    sortOrder = 'DESC';
  } else {
    sortOrder = 'ASC';
  }
  emit('sort-replies', sortParam, sortOrder);
};

const isSortActive = (sort: string) => (sort === props.sortParam ? 'active' : '');
const toggleWatchThread = () => {
  if (watchThread.value) {
    emit('subscribe');
  } else {
    emit('unsubscribe');
  }
};

onMounted(() => {
  nextTick(() => {
    if (threadSubscribed.value || isUserCreator.value) {
      watchThread.value = true;
    }
  });
});

onUpdated(() => {
  const { hash } = route;
  nextTick(() => {
    if (hash && !scrolledToMessage.value) {
      const element = document.querySelector(hash);
      const messageElement = element?.querySelector('.thread-message');
      if (element && messageElement) {
        messageElement.classList.add('scroll-highlight');
        setTimeout(() => {
          messageElement.classList.remove('scroll-highlight');
        }, 1250);
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
        scrolledToMessage.value = true;
      }
    }
  });
});

const createMessagePlaceHolder = computed(() => {
  if (!store.state.projectForum.project.id.length) {
    return '';
  }
  if (user.userId.value === store.state.projectForum.project.creator.userId || projectGuestList.value.map((guest) => guest.id).includes(user.userId.value)) {
    return t('project-forum.placeholder.project-answer-op');
  }
  return t('project-forum.placeholder.project-answer-user');
});

return { __sfc: true,props, emit, store, route, tc, t, tUser, project, createMessageVisible, scrolledToMessage, watchThread, user, threadCreatorProp, numberOfRepliesProp, parentType, threadSubscribed, forumId, threadId, initialMessage, labelReplyCount, noForumReplies, isUserCreator, watchThreadTranslationKey, NoRepliesTranslationKey, isTestProject, hasReplies, hasOPReply, toggleCreateMessageVisible, projectGuestList, isOP, expansionPanelStateChange, getReplyText, postMessage, firstReply, updateMessage, editMessage, sort, isSortActive, toggleWatchThread, createMessagePlaceHolder, Triangle, ThreadRepliesSkeleton, BCXSwitch, ThreadRepliesExpansionPanel, CreateMessage, ThreadReply }
}

})